// src/pages/CloudTab.jsx
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { SectionHeader } from '../components/Nav';

const richStyles = `
  .cloud-rich h2 { font-size:17px; font-weight:600; margin:10px 0 6px; font-family:'Playfair Display',serif; color:var(--sand-text); }
  .cloud-rich h3 { font-size:14px; font-weight:600; margin:8px 0 4px; font-family:'Playfair Display',serif; color:var(--sand-text); }
  .cloud-rich h4 { font-size:12px; font-weight:600; margin:6px 0 3px; font-family:'Playfair Display',serif; color:var(--sand-text); text-transform:uppercase; letter-spacing:0.5px; }
  .cloud-rich p  { margin:0 0 12px; min-height:1.4em; font-size:13px; line-height:1.7; color:var(--sand-muted); }
  .cloud-rich a  { color:#38bdf8; text-decoration:underline; }
  .cloud-rich strong { font-weight:600; color:var(--sand-text); }
  .cloud-rich em { font-style:italic; }
  .cloud-rich img { max-width:100%; border-radius:6px; margin:8px 0; display:block; }
  .cloud-rich iframe { width:100%; aspect-ratio:16/9; height:auto; border-radius:6px; margin:8px 0; display:block; }
  .cloud-rich table { width:100%; border-collapse:collapse; margin:12px 0; font-size:12px; }
  .cloud-rich th { background:var(--sand-surface); color:var(--sand-text); padding:8px 10px; text-align:left; border:0.5px solid var(--sand-border); font-family:'JetBrains Mono',monospace; font-size:10px; letter-spacing:0.5px; text-transform:uppercase; }
  .cloud-rich td { padding:8px 10px; border:0.5px solid var(--sand-border); color:var(--sand-muted); vertical-align:top; }
`;

function RichOrPlain({ content, className }) {
  if (!content) return null;
  const isHTML = content.trim().startsWith('<');
  if (isHTML) return (
    <>
      <style>{richStyles}</style>
      <div className={className} dangerouslySetInnerHTML={{ __html: content }} />
    </>
  );
  return <p style={{ fontSize: 13, color: 'var(--sand-muted)', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{content}</p>;
}

function toAnchor(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function scrollToProvider(name) {
  const id = 'cloud-' + toAnchor(name);
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - 110;
  window.scrollTo({ top: y, behavior: 'smooth' });
}

export default function CloudTab() {
  const [config, setConfig]   = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, 'config', 'cloud'));
        if (snap.exists()) setConfig(snap.data());
      } catch (e) {
        console.error('Failed to load cloud config:', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Hash-based deep linking
  useEffect(() => {
    if (loading) return;
    const hash = window.location.hash;
    if (!hash) return;
    const targetId = hash.replace('#', '');
    let attempts = 0;
    function tryScroll() {
      const el = document.getElementById(targetId);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 110;
        window.scrollTo({ top: y, behavior: 'smooth' });
      } else if (attempts < 5) {
        attempts++;
        setTimeout(tryScroll, 200);
      }
    }
    setTimeout(tryScroll, 150);
  }, [loading]);

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--sand-hint)', fontStyle: 'italic', fontSize: 13 }}>Loading…</div>
  );

  const intro     = config?.intro     || '';
  const providers = config?.providers || [];

  return (
    <div className="fade-up">
      <SectionHeader title="Cloud GPU Options" />

      {/* Intro */}
      {intro && (
        <div style={{ marginBottom: 24 }}>
          <RichOrPlain content={intro} className="cloud-rich" />
        </div>
      )}

      {/* ── TOC ── */}
      {providers.length > 0 && (
        <div style={{ background: 'var(--sand-surface)', border: '1px solid var(--sand-amber-border)', borderRadius: 'var(--radius-lg)', padding: '18px 24px', marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, fontWeight: 600, color: 'var(--sand-text)' }}>
              Cloud providers
            </span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--sand-hint)' }}>
              {providers.length} options
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {providers.map((p, i) => (
              <button
                key={p.id}
                onClick={() => scrollToProvider(p.name)}
                style={{
                  fontFamily: "'Source Serif 4', serif", fontSize: 13,
                  padding: '8px 18px', borderRadius: 'var(--radius-sm)',
                  background: 'var(--sand-amber)', color: '#0e1525',
                  border: 'none', cursor: 'pointer',
                  fontWeight: 500,
                  display: 'flex', alignItems: 'center', gap: 6,
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, opacity: 0.6 }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                {p.name || `Provider ${i + 1}`}
                <span style={{ fontSize: 10, opacity: 0.6 }}>↓</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Provider sections ── */}
      {providers.map((p, i) => (
        <div
          key={p.id}
          id={'cloud-' + toAnchor(p.name)}
          style={{ marginBottom: 36, scrollMarginTop: 110 }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 600, color: 'var(--sand-text)' }}>
              {p.name || `Provider ${i + 1}`}
            </h3>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--sand-hint)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--sand-amber)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--sand-hint)'}
            >↑ top</button>

            {/* Runpod-specific: link to load activity page */}
            {p.showLoadActivity && (
              <button
                onClick={() => navigate('/runpod-activity')}
                style={{
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                  padding: '3px 12px', borderRadius: 'var(--radius-sm)',
                  background: 'var(--sand-teal-bg)', color: '#5eead4',
                  border: '0.5px solid var(--sand-teal-border)',
                  cursor: 'pointer',
                }}
              >
                📊 View GPU load activity →
              </button>
            )}
          </div>

          <div style={{ background: 'var(--sand-surface)', border: '0.5px solid var(--sand-border)', borderRadius: 'var(--radius-lg)', padding: '20px 24px' }}>
            <RichOrPlain content={p.description} className="cloud-rich" />

            {/* CTA button */}
            {p.url && (
              <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <a
                  href={p.url} target="_blank" rel="noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
                    padding: '9px 20px', borderRadius: 'var(--radius-sm)',
                    background: 'var(--sand-amber)', color: '#0e1525',
                    textDecoration: 'none', transition: 'opacity 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  Open {p.name} ↗
                </a>
                {p.isReferral && (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--sand-hint)' }}>
                    Referral link · supports ComfyStack
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      ))}

      {providers.length === 0 && !loading && (
        <p style={{ fontSize: 13, color: 'var(--sand-hint)', fontStyle: 'italic' }}>
          Cloud provider guides coming soon.
        </p>
      )}

      <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--sand-hint)', marginTop: 28, textAlign: 'center', lineHeight: 1.6 }}>
        Some links above are referral links. Using them supports ComfyStack at no extra cost to you.
      </p>
    </div>
  );
}
